import { h, computed, watch, ref } from 'vue'


function formStateClass(state) {
  if (state === true) {
    return ' is-valid'
  }
  if (state === false) {
    return ' is-invalid'
  }
  return ''
}

export default function registerBootstrap5(app) {
  app.component('b-card', {
    setup(_, { slots, attrs }) {
      return () => h('div', { class: ['card', attrs.class] }, slots.default && slots.default())
    },
  })

  app.component('b-card-header', {
    setup(_, { slots, attrs }) {
      return () => h('div', { class: ['card-header', attrs.class] }, slots.default && slots.default())
    },
  })

  app.component('b-card-body', {
    setup(_, { slots, attrs }) {
      return () => h('div', { class: ['card-body', attrs.class] }, slots.default && slots.default())
    },
  })

  app.component('b-form-input', {
    props: {
      modelValue: [String, Number],
      type: {
        type: String,
        default: 'text',
      },
      state: {
        type: [Boolean, null],
        default: null,
      },
    },
    emits: ['update:modelValue', 'input'],
    setup(props, { attrs, emit }) {
      return () =>
        h('input', {
          class: `form-control${formStateClass(props.state)} ${attrs.class || ''}`.trim(),
          value: props.modelValue,
          type: props.type,
          ...attrs,
          onInput: (event) => {
            emit('update:modelValue', event.target.value)
            emit('input', event.target.value)
          },
        })
    },
  })

  app.component('b-form-textarea', {
    props: {
      modelValue: [String, Number],
      state: {
        type: [Boolean, null],
        default: null,
      },
      rows: [String, Number],
    },
    emits: ['update:modelValue', 'input'],
    setup(props, { attrs, emit }) {
      return () =>
        h('textarea', {
          class: `form-control${formStateClass(props.state)} ${attrs.class || ''}`.trim(),
          value: props.modelValue,
          rows: props.rows,
          ...attrs,
          onInput: (event) => {
            emit('update:modelValue', event.target.value)
            emit('input', event.target.value)
          },
        })
    },
  })

  app.component('b-form-select', {
    props: {
      modelValue: [String, Number, Object, Array, Boolean, null],
      options: {
        type: Array,
        default: null,
      },
      state: {
        type: [Boolean, null],
        default: null,
      },
    },
    emits: ['update:modelValue', 'input', 'change'],
    setup(props, { slots, emit, attrs }) {
      const renderOptions = () => {
        if (props.options && props.options.length) {
          return props.options.map((opt) => {
            if (typeof opt === 'object') {
              return h('option', { value: opt.value, disabled: opt.disabled }, opt.text || opt.label)
            }
            return h('option', { value: opt }, opt)
          })
        }
        return slots.default ? slots.default() : []
      }
      return () =>
        h(
          'select',
          {
            class: `form-select${formStateClass(props.state)} ${attrs.class || ''}`.trim(),
            value: props.modelValue,
            ...attrs,
            onInput: (event) => {
              emit('update:modelValue', event.target.value)
              emit('input', event.target.value)
            },
            onChange: (event) => emit('change', event.target.value),
          },
          renderOptions()
        )
    },
  })

  app.component('b-form-select-option', {
    props: {
      value: [String, Number, Object, Boolean, null],
      disabled: Boolean,
    },
    setup(props, { slots }) {
      return () => h('option', { value: props.value, disabled: props.disabled }, slots.default && slots.default())
    },
  })

  app.component('b-form-invalid-feedback', {
    setup(_, { slots }) {
      return () => h('div', { class: 'invalid-feedback d-block' }, slots.default && slots.default())
    },
  })

  app.component('b-input-group', {
    setup(_, { slots, attrs }) {
      return () => h('div', { class: ['input-group', attrs.class] }, slots.default && slots.default())
    },
  })

  app.component('b-input-group-prepend', {
    setup(_, { slots }) {
      return () => h('div', { class: 'input-group-prepend' }, slots.default && slots.default())
    },
  })

  app.component('b-badge', {
    props: {
      variant: {
        type: String,
        default: 'primary',
      },
    },
    setup(props, { slots, attrs }) {
      return () => h('span', { class: [`badge`, `bg-${props.variant}`, attrs.class] }, slots.default && slots.default())
    },
  })

  app.component('b-alert', {
    props: {
      show: {
        type: Boolean,
        default: true,
      },
      variant: {
        type: String,
        default: 'primary',
      },
    },
    setup(props, { slots, attrs }) {
      return () =>
        props.show
          ? h('div', { class: [`alert`, `alert-${props.variant}`, attrs.class], role: 'alert' }, slots.default && slots.default())
          : null
    },
  })

  app.component('b-overlay', {
    props: {
      show: {
        type: Boolean,
        default: false,
      },
    },
    setup(props, { slots, attrs }) {
      return () =>
        h('div', { class: ['position-relative', attrs.class] }, [
          slots.default && slots.default(),
          props.show
            ? h('div', { class: 'position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light bg-opacity-75' }, [
                h('div', { class: 'spinner-border text-primary', role: 'status' }, [
                  h('span', { class: 'visually-hidden' }, 'Loading...'),
                ]),
              ])
            : null,
        ])
    },
  })

  app.component('b-dropdown', {
    props: {
      right: Boolean,
      toggleClass: String,
      variant: String,
      size: String,
      noCaret: Boolean,
    },
    setup(props, { slots, attrs }) {
      const open = ref(false)
      const toggle = () => {
        open.value = !open.value
      }
      const close = () => {
        open.value = false
      }
      return () =>
        h('div', { class: ['dropdown', attrs.class, open.value ? 'show' : null] }, [
          h(
            'button',
            {
              class: [
                'btn',
                props.variant ? `btn-${props.variant}` : 'btn-secondary',
                props.size ? `btn-${props.size}` : null,
                props.toggleClass,
                'dropdown-toggle',
                props.noCaret ? 'dropdown-toggle-no-caret' : null,
              ],
              type: 'button',
              onClick: toggle,
            },
            slots['button-content'] ? slots['button-content']() : slots.default && slots.default()
          ),
          h(
            'div',
            { class: ['dropdown-menu', open.value ? 'show' : null, props.right ? 'dropdown-menu-end' : null] },
            slots.default && slots.default({ close })
          ),
        ])
    },
  })

  app.component('b-dropdown-item', {
    props: {
      to: [String, Object],
      href: String,
    },
    setup(props, { slots, attrs }) {
      const content = slots.default && slots.default()
      if (props.to) {
        return () => h('router-link', { class: ['dropdown-item', attrs.class], to: props.to }, content)
      }
      return () => h('a', { class: ['dropdown-item', attrs.class], href: props.href || '#' }, content)
    },
  })

  app.component('b-dropdown-divider', {
    setup() {
      return () => h('div', { class: 'dropdown-divider' })
    },
  })

  app.component('b-form-group', {
    props: {
      label: String,
      labelFor: String,
      labelColsSm: [String, Number],
      labelAlignSm: String,
      labelSize: String,
    },
    setup(props, { slots, attrs }) {
      return () =>
        h('div', { class: ['mb-3', attrs.class] }, [
          props.label
            ? h(
                'label',
                {
                  for: props.labelFor,
                  class: [
                    props.labelColsSm ? `col-sm-${props.labelColsSm} col-form-label` : null,
                    props.labelAlignSm ? `text-${props.labelAlignSm}` : null,
                    props.labelSize ? `col-form-label-${props.labelSize}` : null,
                  ],
                },
                props.label
              )
            : null,
          slots.default && slots.default(),
        ])
    },
  })

  app.component('b-pagination', {
    props: {
      modelValue: {
        type: Number,
        default: 1,
      },
      totalRows: {
        type: Number,
        default: 0,
      },
      perPage: {
        type: Number,
        default: 10,
      },
      size: String,
      align: String,
    },
    emits: ['update:modelValue'],
    setup(props, { emit, attrs }) {
      const totalPages = computed(() => {
        return props.perPage > 0 ? Math.max(1, Math.ceil(props.totalRows / props.perPage)) : 1
      })
      const setPage = (page) => {
        const newPage = Math.min(Math.max(page, 1), totalPages.value)
        emit('update:modelValue', newPage)
      }
      return () =>
        h(
          'ul',
          { class: ['pagination', props.size ? `pagination-${props.size}` : null, attrs.class] },
          Array.from({ length: totalPages.value }).map((_, idx) => {
            const page = idx + 1
            return h(
              'li',
              { class: ['page-item', page === props.modelValue ? 'active' : null] },
              h(
                'button',
                {
                  class: 'page-link',
                  type: 'button',
                  onClick: () => setPage(page),
                },
                page
              )
            )
          })
        )
    },
  })

  app.component('b-table', {
    props: {
      items: {
        type: Array,
        default: () => [],
      },
      fields: {
        type: Array,
        default: () => [],
      },
      filter: {
        type: String,
        default: '',
      },
      filterIncludedFields: {
        type: Array,
        default: () => [],
      },
      perPage: {
        type: Number,
        default: 10,
      },
      currentPage: {
        type: Number,
        default: 1,
      },
      selectable: Boolean,
      selectMode: String,
      stacked: [String, Boolean],
      showEmpty: Boolean,
      small: Boolean,
    },
    emits: ['filtered', 'row-selected'],
    setup(props, { slots, emit }) {
      const getValueByPath = (item, key) => {
        if (!key) {
          return undefined
        }
        return String(key)
          .split('.')
          .reduce((acc, part) => (acc == null ? undefined : acc[part]), item)
      }

      const filteredItems = computed(() => {
        if (!props.filter) {
          return props.items
        }
        const term = props.filter.toLowerCase()
        return props.items.filter((item) => {
          const keys = props.filterIncludedFields.length
            ? props.filterIncludedFields
            : props.fields.length
              ? props.fields.map((field) => field.key)
              : Object.keys(item)
          return keys.some((key) => String(getValueByPath(item, key) ?? '').toLowerCase().includes(term))
        })
      })

      const pagedItems = computed(() => {
        const start = (props.currentPage - 1) * props.perPage
        return filteredItems.value.slice(start, start + props.perPage)
      })

      watch(
        filteredItems,
        (items) => {
          emit('filtered', items)
        },
        { immediate: true }
      )

      const onRowClick = (item) => {
        if (props.selectable) {
          emitSelection([item])
        }
      }

      const emitSelection = (items) => {
        if (props.selectMode === 'single') {
          items = items.slice(0, 1)
        }
        if (props.selectable) {
          emit('row-selected', items)
        }
      }

      return () => {
        const headerCells = props.fields.map((field) => h('th', { scope: 'col' }, field.label || field.key))
        const bodyRows = pagedItems.value.map((item) => {
          const cells = props.fields.map((field) => {
            const value = getValueByPath(item, field.key)
            const display = typeof field.formatter === 'function' ? field.formatter(value, field.key, item) : value
            if (slots[`cell(${field.key})`]) {
              return h('td', slots[`cell(${field.key})`]({ value: display, item }))
            }
            return h('td', display)
          })
          if (slots['row-details']) {
            cells.push(h('td', slots['row-details']({ item })))
          }
          return h('tr', { onClick: () => onRowClick(item) }, cells)
        })

        const table = h('table', { class: ['table', props.small ? 'table-sm' : null] }, [
          h('thead', [h('tr', headerCells)]),
          h('tbody', bodyRows.length ? bodyRows : [h('tr', [h('td', { colspan: props.fields.length }, 'Keine Daten')])]),
        ])

        return table
      }
    },
  })
}
